/**
 * Sysadmin Models — Create-model wizard (Wave 3 / M6c criterion 2).
 *
 * 4-step React Hook Form flow that authors a new Argus brand:
 *
 *   1. Identity   — brandId, displayName, description, modality
 *   2. Base LLM   — pick from base_llm_registry; preview metadata
 *   3. Dispatch   — temperature/top_p/max_tokens/ctx_size/system_prompt
 *                   + provenance preset whitelist + backend_model_name
 *                   + (optional) attach wrapper functions from library
 *   4. Review     — full payload + Submit
 *
 * Submit fires a single `POST /api/v1/sysadmin/argus-models` with
 * nested `engines: [...]` + `chains: [...]` per the M6c Phase C
 * extended schema. The backend inserts everything in a transaction so
 * partial-create failures don't leave orphan brand rows.
 *
 * On 201 the wizard navigates to /sysadmin/models/$id so the
 * sysadmin can review the new brand. On 409 BRAND_ID_TAKEN the
 * wizard returns the user to step 1 with a field-level error.
 *
 * Per I-M6.1 only the Viaanix engineering team (sysadmin) creates
 * models. The route is gated by the existing requireSysadmin
 * audience check on /sysadmin parent layout.
 */

import { useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Wand2,
  ArrowLeft,
  ArrowRight,
  Check,
  Cpu,
  Sliders,
  ClipboardCheck,
  Sparkles,
  Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useBaseLlmRegistry,
  useWrapperFunctions,
  useCreateArgusModel,
  type BaseLlm,
  type WrapperFunction,
} from '@/lib/use-argus-models';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sysadmin/models/new')({
  component: NewModelWizardPage,
});

// ─── Wizard schema ──────────────────────────────────────────────────────

const PROVENANCE_PRESETS = ['us-only', 'us-eu', 'unrestricted'] as const;

const WizardSchema = z.object({
  // Step 1 — identity
  brandId: z
    .string()
    .min(3, 'At least 3 characters')
    .max(60, 'At most 60 characters')
    .regex(/^[a-z][a-z0-9_]*$/, 'Lowercase letters, digits, underscores; must start with a letter'),
  displayName: z.string().min(1, 'Display name required').max(120),
  description: z.string().min(1, 'Description required'),
  modality: z.enum(['chat', 'vision', 'code', 'reason', 'long', 'image-gen', 'embed']),
  factoryDefault: z.boolean().default(false),
  sortOrder: z.number().int().min(0).max(10000).default(100),

  // Step 2 — base LLM
  baseLlmId: z.string().uuid('Pick a base LLM'),

  // Step 3 — dispatch
  defaultTemperature: z.number().min(0).max(2).default(0.3),
  defaultTopP: z.number().min(0).max(1).default(0.9),
  defaultMaxTokens: z.number().int().positive().optional(),
  defaultCtxSize: z.number().int().positive().default(8192),
  systemPrompt: z.string().default(''),
  guardrailProfile: z.string().min(1).max(60).default('baseline'),
  backendModelName: z
    .string()
    .min(1, 'Backend model name required')
    .max(120)
    .regex(/^[a-z][a-z0-9_]*$/, 'Lowercase letters, digits, underscores'),
  apiBase: z.string().url('Must be a valid URL'),
  provenanceEligibility: z
    .array(z.enum(PROVENANCE_PRESETS))
    .min(1, 'At least one preset required'),
  // Function attachments — wizard surfaces these as an array of
  // { wrapperFunctionId, chainKind, position, isMandatory, failurePolicy }.
  attachedFunctions: z
    .array(
      z.object({
        wrapperFunctionId: z.string().uuid(),
        chainKind: z.enum(['inlet', 'outlet']),
        position: z.number().int().min(0).max(10000),
        isMandatory: z.boolean(),
        failurePolicy: z.enum(['terminal', 'warn']),
      }),
    )
    .default([]),
});

type WizardData = z.infer<typeof WizardSchema>;

const MODALITY_OPTIONS: Array<{ value: WizardData['modality']; label: string; description: string }> = [
  { value: 'chat', label: 'Chat', description: 'General-purpose conversational model' },
  { value: 'vision', label: 'Vision', description: 'Image and screenshot understanding' },
  { value: 'code', label: 'Code', description: 'Code generation, review, refactoring' },
  { value: 'reason', label: 'Reasoning', description: 'Complex reasoning and multi-step logic' },
  { value: 'long', label: 'Long context', description: 'Million-token context for long docs' },
  { value: 'image-gen', label: 'Image generation', description: 'Diffusion-based image generation' },
  { value: 'embed', label: 'Embedding', description: 'Vector embedding for retrieval' },
];

const GUARDRAIL_OPTIONS = ['baseline', 'max-guardrails', 'permissive'];

const STEPS = [
  { num: 1, title: 'Identity', icon: Sparkles },
  { num: 2, title: 'Base LLM', icon: Cpu },
  { num: 3, title: 'Dispatch', icon: Sliders },
  { num: 4, title: 'Review', icon: ClipboardCheck },
];

// ─── Page ───────────────────────────────────────────────────────────────

function NewModelWizardPage() {
  const [step, setStep] = useState(1);
  const navigate = useNavigate();

  const baseLlmsQuery = useBaseLlmRegistry();
  const fnLibraryQuery = useWrapperFunctions();
  const createMutation = useCreateArgusModel();

  // The resolver cast here works around a known typing skew between
  // zod v4 and @hookform/resolvers (the same workaround appears in
  // login.tsx). Runtime behavior is unaffected.
  const form = useForm<WizardData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(WizardSchema as any),
    mode: 'onChange',
    defaultValues: {
      brandId: '',
      displayName: '',
      description: '',
      modality: 'chat',
      factoryDefault: false,
      sortOrder: 100,
      baseLlmId: '',
      defaultTemperature: 0.3,
      defaultTopP: 0.9,
      defaultMaxTokens: undefined,
      defaultCtxSize: 8192,
      systemPrompt: '',
      guardrailProfile: 'baseline',
      backendModelName: '',
      apiBase: 'http://strix-halo-inference:9292/v1',
      provenanceEligibility: ['us-only', 'us-eu', 'unrestricted'],
      attachedFunctions: [],
    },
  });

  const formData = form.watch();
  const selectedBaseLlm = useMemo(
    () => baseLlmsQuery.data?.items.find((b) => b.id === formData.baseLlmId),
    [baseLlmsQuery.data, formData.baseLlmId],
  );

  // Convenience: when the user picks a base LLM, prefill ctx size and
  // backend model name + tighten provenance eligibility per the LLM's
  // origin. They can still override before submit.
  const onPickBaseLlm = (id: string) => {
    form.setValue('baseLlmId', id);
    const llm = baseLlmsQuery.data?.items.find((b) => b.id === id);
    if (!llm) return;
    if (llm.contextWindow > 0) {
      form.setValue('defaultCtxSize', Math.min(llm.contextWindow, 64000));
    }
    if (!formData.backendModelName) {
      // Default to brandId; sysadmin can change before submit.
      form.setValue('backendModelName', formData.brandId || llm.modelKey.replace(/[^a-z0-9_]/g, '_'));
    }
    if (llm.provenance === 'china') {
      form.setValue('provenanceEligibility', ['unrestricted']);
    } else if (llm.provenance === 'us') {
      form.setValue('provenanceEligibility', ['us-only', 'us-eu', 'unrestricted']);
    }
  };

  const goNext = async () => {
    const fields = stepFields(step);
    const ok = await form.trigger(fields as any);
    if (ok) setStep((s) => Math.min(s + 1, STEPS.length));
  };

  const goBack = () => setStep((s) => Math.max(s - 1, 1));

  const onSubmit: SubmitHandler<WizardData> = async (values) => {
    const payload = {
      brandId: values.brandId,
      displayName: values.displayName,
      description: values.description,
      modality: values.modality,
      factoryDefault: values.factoryDefault,
      sortOrder: values.sortOrder,
      engines: [
        {
          baseLlmId: values.baseLlmId,
          roleLabel: 'Primary',
          isDefault: true,
          provenanceEligibility: values.provenanceEligibility,
          defaultTemperature: values.defaultTemperature,
          defaultTopP: values.defaultTopP,
          defaultMaxTokens: values.defaultMaxTokens,
          defaultCtxSize: values.defaultCtxSize,
          systemPrompt: values.systemPrompt,
          guardrailProfile: values.guardrailProfile,
          apiBase: values.apiBase,
          backendModelName: values.backendModelName,
        },
      ],
      chains: values.attachedFunctions.length > 0 ? values.attachedFunctions : undefined,
    };

    try {
      const result = await createMutation.mutateAsync(payload);
      navigate({ to: '/sysadmin/models/$id', params: { id: result.id } });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      // Map server BRAND_ID_TAKEN back to step 1 with a field error.
      if (/brand_id_taken|brandid/i.test(message)) {
        form.setError('brandId', { type: 'server', message: 'A brand with this ID already exists' });
        setStep(1);
      }
    }
  };

  const progressPct = (step / STEPS.length) * 100;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Back link */}
      <div className="flex items-center justify-between">
        <Link
          to="/sysadmin/models"
          className={buttonVariants({ variant: 'ghost', size: 'sm' })}
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to catalog
        </Link>
        <span className="text-xs text-muted-foreground">
          Step {step} of {STEPS.length}
        </span>
      </div>

      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
          <Wand2 className="h-6 w-6" />
          New Argus Model
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Create a new brand the Viaanix engineering team can assign to tenants.
        </p>
      </div>

      {/* Stepper */}
      <Card>
        <CardContent className="py-4">
          <Progress value={progressPct} className="mb-4" />
          <div className="flex items-center justify-between">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const completed = step > s.num;
              const active = step === s.num;
              return (
                <div key={s.num} className="flex items-center gap-2 flex-1">
                  <div
                    className={cn(
                      'flex items-center justify-center h-8 w-8 rounded-full border-2 transition-colors',
                      completed
                        ? 'bg-primary border-primary text-primary-foreground'
                        : active
                          ? 'border-primary text-primary'
                          : 'border-muted text-muted-foreground',
                    )}
                  >
                    {completed ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                  </div>
                  <span
                    className={cn(
                      'text-xs font-medium',
                      active || completed ? 'text-foreground' : 'text-muted-foreground',
                    )}
                  >
                    {s.title}
                  </span>
                  {i < STEPS.length - 1 && (
                    <div className={cn('flex-1 h-px mx-2', completed ? 'bg-primary' : 'bg-muted')} />
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Step content */}
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        onKeyDown={(e) => {
          // Don't accidentally submit when the user presses Enter in step 1-3.
          if (e.key === 'Enter' && step < STEPS.length) e.preventDefault();
        }}
      >
        {step === 1 && <Step1Identity form={form} />}
        {step === 2 && (
          <Step2BaseLlm
            form={form}
            llms={baseLlmsQuery.data?.items ?? []}
            isLoading={baseLlmsQuery.isLoading}
            error={baseLlmsQuery.error}
            selected={selectedBaseLlm}
            onPick={onPickBaseLlm}
          />
        )}
        {step === 3 && (
          <Step3Dispatch
            form={form}
            functions={fnLibraryQuery.data?.items ?? []}
            isLoadingFns={fnLibraryQuery.isLoading}
          />
        )}
        {step === 4 && (
          <Step4Review
            data={formData}
            baseLlm={selectedBaseLlm}
            functions={fnLibraryQuery.data?.items ?? []}
          />
        )}

        {/* Server error from mutation */}
        {createMutation.isError && step === STEPS.length && (
          <Card className="mt-4 border-destructive">
            <CardContent className="py-3 text-sm text-destructive">
              {createMutation.error.message}
            </CardContent>
          </Card>
        )}

        {/* Footer nav */}
        <div className="flex items-center justify-between mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={goBack}
            disabled={step === 1 || createMutation.isPending}
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>

          {step < STEPS.length ? (
            <Button type="button" onClick={goNext}>
              Next
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Create model
              <Check className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}

// ─── Step components ────────────────────────────────────────────────────

function stepFields(step: number): Array<keyof WizardData> {
  if (step === 1) return ['brandId', 'displayName', 'description', 'modality', 'sortOrder'];
  if (step === 2) return ['baseLlmId'];
  if (step === 3)
    return [
      'defaultTemperature',
      'defaultTopP',
      'defaultCtxSize',
      'systemPrompt',
      'guardrailProfile',
      'backendModelName',
      'apiBase',
      'provenanceEligibility',
    ];
  return [];
}

function Step1Identity({ form }: { form: ReturnType<typeof useForm<WizardData>> }) {
  const errors = form.formState.errors;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Identity</CardTitle>
        <CardDescription>
          The brand's stable ID (used in API calls), display name shown to tenants,
          and a tenant-visible description.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Field
          label="Brand ID"
          hint="Lowercase + underscores (e.g. argus_specialist). Tenants pass this in API calls."
          error={errors.brandId?.message}
        >
          <Input {...form.register('brandId')} placeholder="argus_specialist" />
        </Field>
        <Field
          label="Display name"
          hint="Shown to tenants in pickers + the chat header."
          error={errors.displayName?.message}
        >
          <Input {...form.register('displayName')} placeholder="ArgusSpecialist" />
        </Field>
        <Field
          label="Description"
          hint="Plain-English explanation tenants see when picking the model."
          error={errors.description?.message}
        >
          <Textarea
            {...form.register('description')}
            rows={3}
            placeholder="A specialist tuned for…"
          />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Modality" error={errors.modality?.message}>
            <Select
              value={form.watch('modality')}
              onValueChange={(v) => form.setValue('modality', v as WizardData['modality'])}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {MODALITY_OPTIONS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    <div className="flex flex-col">
                      <span className="font-medium">{m.label}</span>
                      <span className="text-xs text-muted-foreground">{m.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Sort order" hint="Lower values appear first in pickers." error={errors.sortOrder?.message}>
            <Input
              type="number"
              {...form.register('sortOrder', { valueAsNumber: true })}
            />
          </Field>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="factory-default"
            checked={form.watch('factoryDefault')}
            onCheckedChange={(v) => form.setValue('factoryDefault', !!v)}
          />
          <Label htmlFor="factory-default" className="text-sm font-normal cursor-pointer">
            Auto-enable for new tenants (factory default)
          </Label>
        </div>
      </CardContent>
    </Card>
  );
}

function Step2BaseLlm({
  form, llms, isLoading, error, selected, onPick,
}: {
  form: ReturnType<typeof useForm<WizardData>>;
  llms: BaseLlm[];
  isLoading: boolean;
  error: Error | null;
  selected: BaseLlm | undefined;
  onPick: (id: string) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Pick a base LLM</CardTitle>
        <CardDescription>
          The model weights this brand will run on. Sysadmin imports base LLMs
          via the catalog seed; tenants never see this layer.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error ? (
          <div className="text-sm text-destructive">Failed to load LLMs: {error.message}</div>
        ) : isLoading ? (
          <div className="text-sm text-muted-foreground animate-pulse">Loading…</div>
        ) : llms.length === 0 ? (
          <div className="text-sm text-muted-foreground">No base LLMs registered. Run the catalog seed first.</div>
        ) : (
          <div className="grid gap-2">
            {llms.map((llm) => {
              const isSelected = form.watch('baseLlmId') === llm.id;
              return (
                <button
                  key={llm.id}
                  type="button"
                  onClick={() => onPick(llm.id)}
                  className={cn(
                    'text-left border rounded-md p-3 transition-colors hover:bg-muted/40',
                    isSelected && 'border-primary bg-primary/5',
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-sm">{llm.displayName}</div>
                      <div className="text-xs text-muted-foreground font-mono mt-0.5 truncate">
                        {llm.modelKey}
                      </div>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <Badge variant="outline" className="text-[10px]">{llm.modality}</Badge>
                        <Badge variant="outline" className="text-[10px]">{llm.provenance}</Badge>
                        <Badge variant="outline" className="text-[10px]">{llm.family}</Badge>
                        {llm.sizeParamsB && (
                          <span className="text-[10px] text-muted-foreground">
                            {llm.sizeParamsB}B params
                          </span>
                        )}
                        {llm.vramGb && (
                          <span className="text-[10px] text-muted-foreground">
                            {llm.vramGb} GB VRAM
                          </span>
                        )}
                        {llm.supportsTools && <Badge variant="secondary" className="text-[10px]">tools</Badge>}
                        {llm.supportsVision && <Badge variant="secondary" className="text-[10px]">vision</Badge>}
                      </div>
                    </div>
                    {isSelected && <Check className="h-5 w-5 text-primary shrink-0" />}
                  </div>
                </button>
              );
            })}
          </div>
        )}
        {selected && (
          <>
            <Separator className="my-4" />
            <div className="text-xs text-muted-foreground space-y-1">
              <div>License: <span className="font-mono">{selected.license}</span></div>
              <div>Provenance origin: {selected.provenanceOrigin}</div>
              <div>Context window: {selected.contextWindow.toLocaleString()} tokens</div>
            </div>
          </>
        )}
        {form.formState.errors.baseLlmId && (
          <div className="text-sm text-destructive mt-2">
            {form.formState.errors.baseLlmId.message}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Step3Dispatch({
  form, functions, isLoadingFns,
}: {
  form: ReturnType<typeof useForm<WizardData>>;
  functions: WrapperFunction[];
  isLoadingFns: boolean;
}) {
  const errors = form.formState.errors;
  const attached = form.watch('attachedFunctions');
  const provenance = form.watch('provenanceEligibility');

  const toggleFn = (fnId: string, chainKind: 'inlet' | 'outlet') => {
    const idx = attached.findIndex(
      (f) => f.wrapperFunctionId === fnId && f.chainKind === chainKind,
    );
    if (idx >= 0) {
      form.setValue(
        'attachedFunctions',
        attached.filter((_, i) => i !== idx),
      );
    } else {
      // Auto-position: append at end of the relevant chain.
      const same = attached.filter((f) => f.chainKind === chainKind);
      const nextPos = same.length === 0 ? 100 : Math.max(...same.map((f) => f.position)) + 100;
      form.setValue('attachedFunctions', [
        ...attached,
        {
          wrapperFunctionId: fnId,
          chainKind,
          position: nextPos,
          isMandatory: true,
          failurePolicy: 'terminal',
        },
      ]);
    }
  };

  const togglePreset = (preset: 'us-only' | 'us-eu' | 'unrestricted') => {
    if (provenance.includes(preset)) {
      form.setValue(
        'provenanceEligibility',
        provenance.filter((p) => p !== preset) as WizardData['provenanceEligibility'],
      );
    } else {
      form.setValue('provenanceEligibility', [...provenance, preset]);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Dispatch settings</CardTitle>
          <CardDescription>
            Defaults the engine uses for every request. Tenants cannot override these
            unless an Agent wrapper supplies values.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Temperature" error={errors.defaultTemperature?.message}>
              <Input
                type="number"
                step="0.05"
                min="0"
                max="2"
                {...form.register('defaultTemperature', { valueAsNumber: true })}
              />
            </Field>
            <Field label="Top-p" error={errors.defaultTopP?.message}>
              <Input
                type="number"
                step="0.05"
                min="0"
                max="1"
                {...form.register('defaultTopP', { valueAsNumber: true })}
              />
            </Field>
            <Field label="Max tokens (optional)">
              <Input
                type="number"
                min="1"
                {...form.register('defaultMaxTokens', {
                  setValueAs: (v) => (v === '' ? undefined : Number(v)),
                })}
              />
            </Field>
            <Field label="Default context size" error={errors.defaultCtxSize?.message}>
              <Input
                type="number"
                min="1"
                {...form.register('defaultCtxSize', { valueAsNumber: true })}
              />
            </Field>
          </div>
          <Field label="System prompt">
            <Textarea {...form.register('systemPrompt')} rows={4} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Guardrail profile" error={errors.guardrailProfile?.message}>
              <Select
                value={form.watch('guardrailProfile')}
                onValueChange={(v) => form.setValue('guardrailProfile', v)}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {GUARDRAIL_OPTIONS.map((g) => (
                    <SelectItem key={g} value={g}>{g}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field
              label="Backend model name"
              hint="Matches the llama-swap profile name."
              error={errors.backendModelName?.message}
            >
              <Input {...form.register('backendModelName')} placeholder="argus_specialist" />
            </Field>
          </div>
          <Field label="API base" error={errors.apiBase?.message}>
            <Input {...form.register('apiBase')} />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Provenance eligibility</CardTitle>
          <CardDescription>
            Which tenant provenance presets can dispatch to this engine. The
            provenance gate enforces this at every request.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 flex-wrap">
            {PROVENANCE_PRESETS.map((preset) => {
              const on = provenance.includes(preset);
              return (
                <button
                  key={preset}
                  type="button"
                  onClick={() => togglePreset(preset)}
                  className={cn(
                    'flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors',
                    on
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-input text-muted-foreground hover:bg-muted/40',
                  )}
                >
                  {on && <Check className="h-3 w-3" />}
                  {preset}
                </button>
              );
            })}
          </div>
          {errors.provenanceEligibility && (
            <div className="text-sm text-destructive mt-2">
              {errors.provenanceEligibility.message as string}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Attach wrapper functions (optional)</CardTitle>
          <CardDescription>
            Pick from the function library. Inlet runs before the LLM call; outlet
            runs after. You can edit the chain composition in detail later.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingFns ? (
            <div className="text-sm text-muted-foreground animate-pulse">Loading library…</div>
          ) : functions.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              No wrapper functions in the library yet. You can attach functions later via
              the brand detail page.
            </div>
          ) : (
            <div className="grid gap-2 max-h-[280px] overflow-y-auto">
              {functions.map((fn) => {
                const inletAttached = attached.some(
                  (a) => a.wrapperFunctionId === fn.id && a.chainKind === 'inlet',
                );
                const outletAttached = attached.some(
                  (a) => a.wrapperFunctionId === fn.id && a.chainKind === 'outlet',
                );
                const canInlet = fn.appliesTo === 'inlet' || fn.appliesTo === 'both';
                const canOutlet = fn.appliesTo === 'outlet' || fn.appliesTo === 'both';
                return (
                  <div
                    key={fn.id}
                    className="border rounded-md p-2 flex items-start justify-between gap-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium">{fn.displayName}</div>
                      <div className="text-xs text-muted-foreground font-mono mt-0.5 truncate">
                        {fn.functionKey}
                      </div>
                      <Badge variant="outline" className="text-[10px] mt-1">{fn.category}</Badge>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        type="button"
                        size="sm"
                        variant={inletAttached ? 'default' : 'outline'}
                        disabled={!canInlet}
                        onClick={() => toggleFn(fn.id, 'inlet')}
                      >
                        {inletAttached && <Check className="h-3 w-3 mr-1" />}
                        Inlet
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={outletAttached ? 'default' : 'outline'}
                        disabled={!canOutlet}
                        onClick={() => toggleFn(fn.id, 'outlet')}
                      >
                        {outletAttached && <Check className="h-3 w-3 mr-1" />}
                        Outlet
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Step4Review({
  data, baseLlm, functions,
}: {
  data: WizardData;
  baseLlm: BaseLlm | undefined;
  functions: WrapperFunction[];
}) {
  const fnById = new Map(functions.map((f) => [f.id, f]));
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Review &amp; create</CardTitle>
        <CardDescription>
          Double-check the configuration. Submitting will create the brand,
          its primary engine, and any chain entries you attached.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5 text-sm">
        <ReviewSection title="Identity">
          <ReviewRow label="Brand ID" value={<code className="font-mono">{data.brandId}</code>} />
          <ReviewRow label="Display name" value={data.displayName} />
          <ReviewRow label="Modality" value={<Badge variant="outline">{data.modality}</Badge>} />
          <ReviewRow label="Description" value={data.description} />
          <ReviewRow label="Sort order" value={data.sortOrder} />
          <ReviewRow
            label="Factory default"
            value={data.factoryDefault ? 'Yes' : 'No'}
          />
        </ReviewSection>
        <Separator />
        <ReviewSection title="Base LLM">
          {baseLlm ? (
            <>
              <ReviewRow label="Display name" value={baseLlm.displayName} />
              <ReviewRow label="Model key" value={<code className="font-mono">{baseLlm.modelKey}</code>} />
              <ReviewRow label="Family" value={baseLlm.family} />
              <ReviewRow label="Provenance" value={baseLlm.provenance} />
              <ReviewRow label="License" value={baseLlm.license} />
            </>
          ) : (
            <div className="text-muted-foreground text-xs">No base LLM selected.</div>
          )}
        </ReviewSection>
        <Separator />
        <ReviewSection title="Dispatch">
          <ReviewRow label="Temperature" value={data.defaultTemperature} />
          <ReviewRow label="Top-p" value={data.defaultTopP} />
          <ReviewRow label="Max tokens" value={data.defaultMaxTokens ?? 'unlimited'} />
          <ReviewRow label="Context size" value={data.defaultCtxSize.toLocaleString()} />
          <ReviewRow label="Backend model name" value={<code className="font-mono">{data.backendModelName}</code>} />
          <ReviewRow label="API base" value={<code className="font-mono">{data.apiBase}</code>} />
          <ReviewRow label="Guardrail profile" value={data.guardrailProfile} />
          <ReviewRow
            label="Provenance eligibility"
            value={data.provenanceEligibility.map((p) => (
              <Badge key={p} variant="outline" className="mr-1 text-[10px]">{p}</Badge>
            ))}
          />
          {data.systemPrompt && (
            <div className="mt-2">
              <div className="text-xs text-muted-foreground mb-1">System prompt</div>
              <pre className="text-xs font-mono whitespace-pre-wrap rounded bg-muted/40 p-2 max-h-48 overflow-auto">
                {data.systemPrompt}
              </pre>
            </div>
          )}
        </ReviewSection>
        <Separator />
        <ReviewSection title={`Wrapper chain (${data.attachedFunctions.length})`}>
          {data.attachedFunctions.length === 0 ? (
            <div className="text-muted-foreground text-xs">
              No functions attached. The brand will use only the platform's
              mandatory wrapper chain (no custom additions).
            </div>
          ) : (
            <div className="space-y-1">
              {data.attachedFunctions
                .slice()
                .sort((a, b) =>
                  a.chainKind === b.chainKind ? a.position - b.position : a.chainKind.localeCompare(b.chainKind),
                )
                .map((entry, idx) => {
                  const fn = fnById.get(entry.wrapperFunctionId);
                  return (
                    <div key={idx} className="flex items-center gap-2 text-xs">
                      <Badge
                        variant="outline"
                        className={cn(
                          'text-[10px]',
                          entry.chainKind === 'inlet'
                            ? 'border-blue-300 text-blue-700'
                            : 'border-purple-300 text-purple-700',
                        )}
                      >
                        {entry.chainKind}
                      </Badge>
                      <span className="tabular-nums text-muted-foreground">{entry.position}</span>
                      <span className="font-medium">{fn?.displayName ?? entry.wrapperFunctionId}</span>
                      {entry.isMandatory && (
                        <Badge variant="default" className="text-[10px]">mandatory</Badge>
                      )}
                    </div>
                  );
                })}
            </div>
          )}
        </ReviewSection>
      </CardContent>
    </Card>
  );
}

function ReviewSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
        {title}
      </h3>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[160px_1fr] gap-3 items-start">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span>{value}</span>
    </div>
  );
}

function Field({
  label, hint, error, children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {error && <div className="text-xs text-destructive">{error}</div>}
      {!error && hint && <div className="text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}
